/**
 * figSpecSchema — figspec 명세 문법을 표 하나로 적어 둔 것.
 *
 * 계약 §2 「figspec 명세 문법」 + **§9-1 문법 v2**(2026-08-30). 그릴 수 있는 원소가 10종이고
 * 원소마다 꾸미기 옵션이 붙는데, 종류마다 폼을 손으로 쓰면 같은 코드가 열 벌이 되고
 * 백엔드가 문법을 늘릴 때마다 열 군데를 고쳐야 한다. 여기 한 줄만 늘리면 화면이 따라온다.
 *
 * ── 칸이 둘로 갈린다(2026-08-30) ──
 * `fields` = **형태를 정하는 값**(좌표·식). 늘 보인다.
 * `opts`   = **꾸미기**(점선·굵기·화살표·같은 길이·채우기·빗금…). 값이 있을 때만 펴고, 없으면 접는다.
 * 왜 가르나: §9-3 이 변환 프롬프트에서 「직각·각·점선·빗금·화살표는 생략」을 **필수**로 바꿨다.
 * 그래서 앞으로 AI 가 넣어 보내는 그림이 늘어나는데, 전부 한 줄에 펴면 선분 하나가 11칸이 되고
 * 전부 접으면 AI 가 넣은 것이 접힌 채라 지금과 똑같이 안 보인다. **값이 있으면 편다**가 그 사이다.
 *
 * 규칙(계약 §2·§9-1·**§9-4**(2026-08-31) — 화면 안내 문구의 출처):
 * - expr 은 변수 x 와 `+ - * / ^ ( ) sin cos tan sqrt abs exp ln log pi e` 만.
 *   숫자 붙은 이름은 금지다(`log2` ✗ → `ln(x)/ln(2)` ○).
 * - label·t 는 **12자 이내**(§9-1 에서 6자 → 12자). **한글도 쓸 수 있다** — §9-4 가
 *   「종전 한글은 alt 로만 규칙 폐지」로 열었다. 그림 속 실제 문구(안내판 등)를 그대로 적는 자리다.
 * - 라벨은 서버가 수식으로 옮겨 그린다 — `x^2`→x² · `a_1`→a₁ · `sqrt(2)`→√2 · `pi`→π · `<=`→≤ ·
 *   **`2/3`→⅔**(통속분수). `$…$`·`\mathrm` 같은 **LaTeX 구분자·명령은 벗겨진다**.
 *   숫자 분수는 세로로 조판된다(2 위·가로선·3 아래). 기본 글자 크기는 **13px**·수학 세리프.
 * - 라벨 위치는 서버가 겹침을 피해 자동으로 옮긴다. dx/dy 는 그 자리가 비었을 때만 존중된다.
 *   **음수 dx 는 오른쪽 정렬**(라벨 끝이 그 자리). §9-4 로 회피 대상이 **자유 라벨·직각/각 표시까지**
 *   넓어졌다 — 이제 안 피하는 라벨이 없다.
 * - 그릴 원소가 하나도 없으면 서버가 기각한다.
 */

/**
 * 여러 원소가 함께 쓰는 꾸미기 칸(계약 §9-1 「공통」). 같은 정의를 열 번 적으면 한 벌만 고쳐져 갈린다.
 * 참거짓 칸은 **기본이 꺼짐**이다 — 체크를 안 한 그림에 점선이 그어지면 안 된다.
 */
const OPT_DASHED = { k: 'dashed', label: '점선', bool: true };
const OPT_WIDTH = { k: 'width', label: '굵기', w: 'sm', num: true, ph: '0.5~4' };
const OPT_ARROW = {
  k: 'arrow',
  label: '화살표',
  sel: [['', '없음'], ['end', '끝'], ['start', '시작'], ['both', '양쪽']],
};
const OPT_FILL = { k: 'fill', label: '채우기', bool: true };
/**
 * 채움 **진하기**(계약 §9-1 「`fill` 의 `opacity` · 0.05~0.9 · 기본 0.12」, 2026-08-31 보강).
 * 계약이 「원본이 진한 음영일 때 0.3~0.5」라고 값까지 적어 두어 placeholder 에 그대로 싣는다 —
 * 범위만 적으면 사람이 어느 쪽 끝을 쓸지 매번 다시 판단한다.
 * `fill` 과 짝이지만 **따로 둔다**: 서버가 `sectors` 에서는 `fill` 없이 `opacity` 만도 받는다.
 */
const OPT_OPACITY = {
  k: 'opacity', label: '진하기', w: 'sm', num: true, ph: '0.12',
  title: '0.05~0.9 · 기본 0.12 · 원본이 진한 음영이면 0.3~0.5',
};
const OPT_HATCH = { k: 'hatch', label: '빗금', bool: true };
/** 이 원소의 라벨만 크게·작게(계약 §11). 비우면 최상위 `font_size`, 그것도 없으면 서버 기본 13px. */
const OPT_SIZE = { k: 'size', label: '글자 크기', w: 'sm', num: true, ph: '13', title: '8~24 · 이 원소의 라벨만' };
/** `size` 를 받는 요소 — 회신 §1 의 표 그대로. 라벨이 없는 polygons·shade 는 빠진다. */
const FIG_SIZE_KEYS = ['points', 'labels', 'segments', 'angles', 'curves', 'vlines', 'hlines', 'circles', 'sectors', 'arcs'];

/**
 * num: 숫자 칸 · bool: 참거짓(기본 꺼짐, `defTrue` 면 켜짐) · pts: 꼭짓점 목록 · pair: 좌표 한 쌍
 * · nums: 숫자 목록 · sel: 고르는 칸 · w: 칸 너비(sm 4rem / md 6rem / lg 자람)
 *
 * `title` 은 **화면에 글자로 뜨지 않는 설명**이다(2026-09-06). 폼에서 안내 문장을 걷어내면서
 * (FigSpecForm 머리주석 ①), 라벨만으로는 뜻이 안 서는 칸의 설명이 갈 자리로 삼았다 — 마우스를
 * 올렸을 때만 뜬다. `hint`(원소 설명)도 같은 이유로 화면에서 내려와 [추가] 버튼의 title 로만 쓴다.
 */
export const FIG_ELEMENTS = [
  {
    key: 'curves', name: '곡선', hint: 'y = f(x) 꼴의 함수',
    fields: [
      { k: 'expr', label: '식', w: 'lg', ph: 'x^2-2*x' },
      { k: 'label', label: '이름', w: 'md', ph: 'y=f(x)' },
      { k: 'labelX', label: '이름 x', w: 'sm', num: true },
    ],
    opts: [OPT_DASHED, OPT_WIDTH, OPT_ARROW],
  },
  {
    key: 'vlines', name: '세로선', hint: 'x = 상수',
    fields: [{ k: 'x', label: 'x', w: 'sm', num: true }, { k: 'label', label: '이름', w: 'md', ph: 'x=2' }],
    opts: [OPT_DASHED, OPT_WIDTH],
  },
  {
    key: 'hlines', name: '가로선', hint: 'y = 상수',
    fields: [{ k: 'y', label: 'y', w: 'sm', num: true }, { k: 'label', label: '이름', w: 'md', ph: 'y=1' }],
    opts: [OPT_DASHED, OPT_WIDTH],
  },
  {
    key: 'segments', name: '선분', hint: '두 점을 잇는다',
    fields: [
      { k: 'x1', label: 'x1', w: 'sm', num: true }, { k: 'y1', label: 'y1', w: 'sm', num: true },
      { k: 'x2', label: 'x2', w: 'sm', num: true }, { k: 'y2', label: 'y2', w: 'sm', num: true },
    ],
    opts: [
      // 변 길이 표기 — 중점 곁에 붙고 서버가 겹침을 피한다(계약 §9-1)
      { k: 'label', label: '길이', w: 'md', ph: 'sqrt(2)' },
      { k: 'dx', label: 'dx', w: 'sm', num: true, title: '길이 글자를 밀어 둘 거리(px)' },
      { k: 'dy', label: 'dy', w: 'sm', num: true, title: '길이 글자를 밀어 둘 거리(px)' },
      // 같은 길이 표시 — 중점에 짧은 눈금을 겹수만큼(1~3)
      { k: 'ticks', label: '같은 길이', w: 'sm', num: true, ph: '1~3' },
      /**
       * 끝까지 늘이기(계약 §9-4 `extend: "both"|"start"|"end"`, 2026-08-31).
       * 두 점만 찍어 두면 서버가 **그림 범위 끝까지** 그 직선을 잇는다 —
       * 「끝없는 직선」을 그리려고 범위 밖 좌표를 손으로 계산하던 것이 없어진다.
       */
      { k: 'extend', label: '늘이기', title: '두 점을 지나는 직선을 그림 범위 끝까지 잇는다',
        sel: [['', '안 늘임'], ['both', '양쪽'], ['start', '시작 쪽'], ['end', '끝 쪽']] },
      OPT_DASHED, OPT_WIDTH, OPT_ARROW,
    ],
  },
  {
    key: 'circles', name: '원', hint: '중심과 반지름',
    fields: [
      { k: 'cx', label: '중심 x', w: 'sm', num: true }, { k: 'cy', label: '중심 y', w: 'sm', num: true },
      { k: 'r', label: '반지름', w: 'sm', num: true },
    ],
    opts: [{ k: 'label', label: '이름', w: 'sm', ph: 'O' }, OPT_DASHED, OPT_WIDTH, OPT_FILL, OPT_OPACITY, OPT_HATCH],
  },
  {
    key: 'polygons', name: '다각형', hint: '꼭짓점을 "0,0 4,0 2,3" 처럼',
    fields: [
      { k: 'pts', label: '꼭짓점', w: 'lg', pts: true, ph: '0,0 4,0 2,3' },
      // 종전부터 있던 칸이라 기본이 켜짐이다(계약 §2 의 예시가 close:true) — 새 참거짓 칸과 다르다
      { k: 'close', label: '닫기', bool: true, defTrue: true },
    ],
    opts: [OPT_DASHED, OPT_WIDTH, OPT_FILL, OPT_OPACITY, OPT_HATCH],
  },
  {
    key: 'points', name: '점', hint: '이름과 위치. dx·dy 는 이름을 밀어 둘 거리(px)',
    fields: [
      { k: 'x', label: 'x', w: 'sm', num: true }, { k: 'y', label: 'y', w: 'sm', num: true },
      { k: 'label', label: '이름', w: 'sm', ph: 'A' },
      { k: 'dx', label: 'dx', w: 'sm', num: true, title: '이름을 밀어 둘 거리(px) · 음수 dx 는 오른쪽 정렬' },
      { k: 'dy', label: 'dy', w: 'sm', num: true, title: '이름을 밀어 둘 거리(px)' },
    ],
    // 빈 점 = 제외점(그 자리를 포함하지 않는다는 표시)
    opts: [{ k: 'hollow', label: '빈 점', bool: true }],
  },
  {
    key: 'labels', name: '글자', hint: '아무 자리에 글자만',
    fields: [
      { k: 'x', label: 'x', w: 'sm', num: true }, { k: 'y', label: 'y', w: 'sm', num: true },
      { k: 't', label: '글자', w: 'md', ph: 'α' },
    ],
    opts: [{ k: 'anchor', label: '정렬', sel: [['', '왼쪽'], ['middle', '가운데'], ['end', '오른쪽']] }],
  },
  /**
   * 아래 둘이 §9-1 로 새로 생긴 원소다.
   *
   * 각(angles) — 꼭짓점 `at` 에서 `from`·`to` **방향** 사이의 작은 쪽에 호를 그린다.
   * 세 값이 전부 좌표 한 쌍이라 칸 타입이 `pair` 다(다각형의 `pts` 는 여러 쌍, 이쪽은 한 쌍).
   * [직각]을 켜면 호 대신 픽셀 정사각으로 그린다 — 수학에서 직각은 호가 아니라 네모다.
   */
  {
    key: 'angles', name: '각', hint: '꼭짓점에서 두 방향 사이. 직각은 [직각]을 켠다',
    fields: [
      { k: 'at', label: '꼭짓점', w: 'md', pair: true, ph: '0,0' },
      { k: 'from', label: '한쪽', w: 'md', pair: true, ph: '4,0' },
      { k: 'to', label: '다른쪽', w: 'md', pair: true, ph: '2,3' },
    ],
    opts: [
      { k: 'right', label: '직각', bool: true },
      { k: 'label', label: '이름', w: 'sm', ph: 'theta' },
      { k: 'count', label: '겹수', w: 'sm', num: true, ph: '1~3' },
      { k: 'r', label: '호 크기', w: 'sm', num: true, ph: '14' },
    ],
  },
  /**
   * 호(arcs) — 중심·반지름에 각도 구간을 더한 것. `from`·`to` 가 **도(°)이고 반시계**다.
   * 같은 이름의 키를 각(angles)은 좌표로 쓴다 — 원소가 다르면 뜻도 다르다(계약 §9-1 표 그대로).
   *
   * 힌트에서 「부채꼴도 이것으로」를 뺐다(2026-08-31) — 부채꼴이 제 원소로 생겼다(아래 sectors).
   * 호는 **선**이고 부채꼴은 **면**이라, 색칠할 것이 있으면 이쪽이 아니다.
   */
  {
    key: 'arcs', name: '호', hint: '중심·반지름과 각도 구간(도, 반시계). 선만 그린다',
    fields: [
      { k: 'cx', label: '중심 x', w: 'sm', num: true }, { k: 'cy', label: '중심 y', w: 'sm', num: true },
      { k: 'r', label: '반지름', w: 'sm', num: true },
      { k: 'from', label: '시작°', w: 'sm', num: true, title: '도(°) · 반시계' },
      { k: 'to', label: '끝°', w: 'sm', num: true, title: '도(°) · 반시계' },
    ],
    opts: [{ k: 'label', label: '이름', w: 'sm' }, OPT_DASHED, OPT_WIDTH, OPT_ARROW],
  },
  /**
   * 부채꼴(sectors) — 계약 §9-1 표에 2026-08-31 로 붙은 원소.
   * 「중심각 색칠 영역 — **다각형 근사 불필요**」가 붙은 이유다. 종전에는 부채꼴을 그리려면
   * 호 위의 점을 여럿 찍어 다각형으로 흉내 내야 했고, 그러면 곡선이 각지고 반지름을 고칠 때마다
   * 점을 전부 다시 계산해야 했다.
   *
   * 칸 구성은 호(arcs)와 같다 — 다른 것은 **면이라 채우기·진하기·빗금이 붙는다**는 점이다.
   */
  {
    key: 'sectors', name: '부채꼴', hint: '중심·반지름과 각도 구간(도, 반시계). 안을 칠한다',
    fields: [
      { k: 'cx', label: '중심 x', w: 'sm', num: true }, { k: 'cy', label: '중심 y', w: 'sm', num: true },
      { k: 'r', label: '반지름', w: 'sm', num: true },
      { k: 'from', label: '시작°', w: 'sm', num: true, title: '도(°) · 반시계' },
      { k: 'to', label: '끝°', w: 'sm', num: true, title: '도(°) · 반시계' },
    ],
    opts: [{ k: 'label', label: '이름', w: 'sm' }, OPT_FILL, OPT_OPACITY, OPT_HATCH],
  },
]
  /**
   * **원소별 글자 크기**(계약 §11 · `BE-20260904-reply-figure-fontsize.md` §1) — 그 라벨만
   * 최상위 `font_size` 를 덮는다. 8~24 밖은 서버가 관문 없이 **클램프**한다(최상위와 다르다:
   * 그쪽은 409 `invalid_spec`).
   *
   * 열 군데 배열에 손으로 넣지 않고 목록 하나로 붙인다 — 손으로 넣으면 하나가 빠져도
   * 아무도 모르고, 요소가 늘 때마다 같은 실수를 되풀이한다.
   * 빠지는 둘(`polygons`·`shade`)은 **라벨이 없어** 글자 크기를 가질 수 없다.
   */
  .map((e) => (FIG_SIZE_KEYS.includes(e.key) ? { ...e, opts: [...(e.opts || []), OPT_SIZE] } : e));

/** shade 는 배열이 아니라 객체 하나다 — 따로 다룬다(계약 §2). */
export const FIG_SHADE_FIELDS = [
  { k: 'upper', label: '위 곡선', w: 'lg', ph: 'x' },
  { k: 'lower', label: '아래 곡선', w: 'lg', ph: 'x^2-2*x' },
  { k: 'from', label: '왼쪽 x', w: 'sm', num: true },
  { k: 'to', label: '오른쪽 x', w: 'sm', num: true },
];

/**
 * 칠하기의 꾸미기(계약 §9-1 — fill·hatch 는 circles·polygons·shade 셋에 붙는다).
 * 2026-08-31 로 **진하기(opacity)** 가 함께 붙는다 — 채우는 자리는 전부 같은 칸을 갖는다.
 */
export const FIG_SHADE_OPTS = [OPT_FILL, OPT_OPACITY, OPT_HATCH];

/**
 * 축의 꾸미기(계약 §9-1 최상위 키). 눈금은 **적힌 값만** 숫자로 표기된다 —
 * 빈 목록과 「눈금 없음」이 같은 뜻이라 값이 없으면 키를 싣지 않는다(pruneSpec).
 */
/**
 * 그림 전체의 글자 크기(계약 §11 · 2026-09-04). 비우면 키가 빠지고 서버 기본 **13px** 이라,
 * **이미 저장된 그림은 한 글자도 달라지지 않는다**(회신 §2 「소급 없음」).
 *
 * 절대 px 인 이유는 요청서 §3-1 이 적은 그대로다 — 기본값이 바뀌어도 저장된 값은 그대로 렌더된다.
 * 8~24 밖이면 서버가 409 `invalid_spec` 로 거절하고 그 사유가 `issues` 에 실려 온다.
 */
export const FIG_FONT_FIELDS = [
  { k: 'font_size', label: '글자 크기', w: 'sm', num: true, ph: '13', title: '8~24 · 비우면 13' },
];

export const FIG_AXIS_FIELDS = [
  { k: 'xticks', label: '가로 눈금', w: 'md', nums: true, ph: '1 3' },
  { k: 'yticks', label: '세로 눈금', w: 'md', nums: true, ph: '1 2' },
];

/**
 * 그릴 원소가 하나라도 있는가 — 없으면 서버가 기각한다(계약 §2 끝줄).
 *
 * ⚠ **`labels` 는 여기 들지 않는다.** 계약 §2 가 꼽은 것은 일곱이고
 * (`curves/segments/circles/polygons/points/vlines/hlines`), 글자만 있는 명세는
 * 「그릴 원소 없음(labels만인 명세 기각)」으로 200 ok:false 가 온다. `shade`(칠하기)도
 * 혼자서는 그릴 것이 못 된다 — 위아래 경계가 될 곡선이 있어야 뜻이 선다.
 *
 * ⚠ **`angles`·`arcs`·`sectors` 는 들어 있다.** §9-1 이 이 셋을 더하면서 §2 의 기각 목록을 갱신하지
 * 않아, 서버가 이 셋만 있는 명세를 받는지 계약에 없다(→ 백엔드 질의). 그래도 여기 넣는 편이 맞다 —
 * 저장 잠금은 `preview.ok === true && hasDrawable(...)` **둘의 곱**이고(FigureModal.canSave)
 * 진짜 심판은 서버의 렌더 응답이다. 여기서 빼면 서버가 받아 주는 그림까지 화면이 먼저 막고,
 * 넣어도 서버가 기각하면 버튼은 그대로 잠긴 채 서버의 issues 가 이유를 말한다. **모르는 것으로
 * 막지 않는다**는 이 저장소의 규칙과 같다(figureCapabilities 주석).
 */
export const DRAWABLE_KEYS = FIG_ELEMENTS.map((e) => e.key).filter((k) => k !== 'labels');

/**
 * 위 목록의 사람 이름 — 저장이 막혔을 때 「무엇이 하나는 있어야 하는지」를 말하는 데 쓴다.
 * 문구에 손으로 적어 두면 원소를 늘릴 때마다 갈린다(§9-1 을 붙이며 실제로 갈려 있었다).
 */
export const DRAWABLE_NAMES = FIG_ELEMENTS.filter((e) => e.key !== 'labels').map((e) => e.name).join('·');

export function hasDrawable(spec) {
  if (spec == null || typeof spec !== 'object') return false;
  return DRAWABLE_KEYS.some((k) => Array.isArray(spec[k]) && spec[k].length > 0);
}

/** "0,0 4,0 2,3" ↔ [[0,0],[4,0],[2,3]] */
export function ptsToText(pts) {
  if (!Array.isArray(pts)) return '';
  return pts.map((p) => (Array.isArray(p) ? p.join(',') : '')).filter(Boolean).join(' ');
}

export function textToPts(text) {
  return String(text || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((pair) => pair.split(',').map((n) => Number(n)))
    .filter((p) => p.length === 2 && p.every((n) => Number.isFinite(n)));
}

/**
 * "0,0" ↔ [0,0] — 각(angles)의 at·from·to 한 쌍.
 *
 * 못 읽은 값은 **원문을 그대로 남긴다.** 숫자 칸이 이미 그렇게 한다(toValue) — 조용히 버리면
 * 사용자는 자기가 친 것이 어디로 갔는지 모르고, 서버는 「at 없음」이라 답해 원인을 가린다.
 * 원문을 실어 보내면 서버가 형식을 짚어 준다.
 */
export function pairToText(v) {
  if (Array.isArray(v)) return v.join(',');
  return v == null ? '' : String(v);
}

export function textToPair(text) {
  const s = String(text ?? '').trim();
  if (s === '') return undefined;
  const parts = s.split(',');
  // 빈 조각을 반드시 걸러야 한다 — `Number('') === 0` 이라, 치는 도중의 "0," 가 조용히 [0,0] 이 된다.
  if (parts.length === 2 && parts.every((p) => p.trim() !== '')) {
    const nums = parts.map((p) => Number(p.trim()));
    if (nums.every(Number.isFinite)) return nums;
  }
  return text;
}

/** "1 3" ↔ [1,3] — 축 눈금. 쉼표로 적어도 받는다(사람이 둘을 섞어 친다). */
export function numsToText(v) {
  if (!Array.isArray(v)) return v == null ? '' : String(v);
  return v.join(' ');
}

export function textToNums(text) {
  return String(text || '')
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean)
    .map((n) => Number(n))
    .filter((n) => Number.isFinite(n));
}

/**
 * 빈 값을 걷어낸 명세를 만든다.
 * 빈 문자열을 그대로 보내면 서버가 「식이 비었다」로 기각하는데, 사용자는 방금 지운 칸이
 * 원인인 줄 모른다. 그래서 **비어 있는 키는 아예 빼고** 보낸다.
 *
 * ⚠ `false` 는 남긴다 — 「점선 끔」과 「점선을 안 정함」이 서버에서는 같지만, `axes:false`
 * (축 없는 순수 도형)처럼 **꺼짐이 뜻인 키**가 계약에 있다. 그 하나 때문에 전부 남긴다.
 */
export function pruneSpec(spec) {
  if (spec == null || typeof spec !== 'object') return spec;
  const out = {};
  Object.keys(spec).forEach((k) => {
    const v = spec[k];
    if (v == null) return;
    if (typeof v === 'string' && v.trim() === '') return;
    if (Array.isArray(v)) {
      const arr = v.map(pruneOne).filter((x) => x != null);
      if (arr.length > 0) out[k] = arr;
      return;
    }
    if (typeof v === 'object') {
      const o = pruneOne(v);
      if (o != null) out[k] = o;
      return;
    }
    out[k] = v;
  });
  return out;
}

function pruneOne(v) {
  if (v == null) return null;
  if (Array.isArray(v)) return v;
  if (typeof v !== 'object') return v;
  const o = {};
  Object.keys(v).forEach((k) => {
    const x = v[k];
    if (x == null) return;
    if (typeof x === 'string' && x.trim() === '') return;
    o[k] = x;
  });
  return Object.keys(o).length > 0 ? o : null;
}
